import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  exhaustMap,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { LocalStorageService } from '../services/local.storage.service';
import { BackendErrorCode } from '../constants/backend-error-code.constants';
import { CoreService } from '../services/core.service';
import { SessionLifecycle } from '../services/session-lifecycle.service';
import { MatDialog } from '@angular/material/dialog';

const HttpResponseType = { Blob: 'blob', ArrayBuffer: 'arraybuffer' } as const;

export const SILENT_ERROR = new HttpContextToken<boolean>(() => false);
/** The toast for a backend code that has no `errors.responseErrors` text, instead of the generic one. */
export const ERROR_FALLBACK = new HttpContextToken<string | null>(() => null);
export const SKIP_AUTH_REDIRECT = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private coreService = inject(CoreService);
  private sessionLifecycle = inject(SessionLifecycle);
  private lsService = inject(LocalStorageService);
  private dialog = inject(MatDialog);

  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipAuthRedirect = request.context.get(SKIP_AUTH_REDIRECT);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status !== HttpStatusCode.Unauthorized ||
          (error.status === HttpStatusCode.Unauthorized &&
            error.error?.message?.toLowerCase() === BackendErrorCode.InvalidRefreshToken) ||
          this.isGoogleAuthRequest(request)
        ) {
          this.reportError(request, error);
          return throwError(() => error);
        }

        // Call auth.refreshAccessToken(this is an Observable that will be returned)
        const refreshToken: string | null = this.lsService.get('refreshToken');
        const deviceId: string | null = this.lsService.get('deviceId');

        if (!refreshToken || !deviceId) {
          this.clearAuthData();

          if (skipAuthRedirect) {
            return throwError(() => error);
          }

          this.dialog.closeAll();
          return this.coreService.navigateToLogin();
        }

        // The retry runs past this interceptor, so it handles its own failure: a 401 means even the
        // fresh token is refused and the session is over; anything else is the request's own error.
        const retry = () =>
          next.handle(this.addAuthenticationToken(request)).pipe(
            catchError((retryError: HttpErrorResponse) => {
              if (retryError.status === HttpStatusCode.Unauthorized) {
                this.clearAuthData();
                return this.leaveForLogin(retryError, skipAuthRedirect);
              }

              this.reportError(request, retryError);
              return throwError(() => retryError);
            }),
          );

        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject.pipe(
            filter((result) => result !== null),
            take(1),
            // Only the refresh failing lands here (see `releaseWaitingRequests`), not the retry.
            catchError((refreshError: HttpErrorResponse) =>
              this.leaveForLogin(refreshError, skipAuthRedirect),
            ),
            switchMap(retry),
          );
        } else {
          this.refreshTokenInProgress = true;
          // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
          this.refreshTokenSubject.next(null);

          return this.coreService.updateToken(refreshToken).pipe(
            // Before `exhaustMap`: a failed retry after a good refresh is the request's own error,
            // not a reason to end the session.
            catchError((refreshError: HttpErrorResponse) => {
              this.clearAuthData();
              this.refreshTokenInProgress = false;
              this.releaseWaitingRequests(refreshError);
              return this.leaveForLogin(refreshError, skipAuthRedirect);
            }),
            exhaustMap((token: any) => {
              this.refreshTokenSubject.next(token);
              this.refreshTokenInProgress = false;
              return retry();
            }),
          );
        }
      }),
    );
  }

  /** Toasts a failed request's backend code, unless the request asked to stay silent. */
  private reportError(request: HttpRequest<any>, error: HttpErrorResponse) {
    if (request.context.get(SILENT_ERROR)) return;

    const fallbackKey = request.context.get(ERROR_FALLBACK);

    if (error.error?.message) {
      this.coreService.proceedHTTPError(error, error.error.message, error.error?.data, fallbackKey);
    }

    if (
      error?.error &&
      (request instanceof Blob || request.responseType == HttpResponseType.Blob)
    ) {
      (error.error as Blob).text().then((res) => {
        if (res) {
          const err = JSON.parse(res);
          err?.message &&
            this.coreService.proceedHTTPError(error, err.message, err.data, fallbackKey);
        }
      });
    }

    if (error?.error && request.responseType == HttpResponseType.ArrayBuffer) {
      const enc = new TextDecoder('utf-8');
      const dec = enc.decode(error.error);
      const err = JSON.parse(dec);
      err?.message && this.coreService.proceedHTTPError(error, err.message, err.data, fallbackKey);
    }
  }

  /** The refresh failed: sends the user to login unless this request opted out, and fails it. */
  private leaveForLogin(error: HttpErrorResponse, skipAuthRedirect: boolean) {
    if (!skipAuthRedirect) {
      this.dialog.closeAll();
      this.coreService.navigateToLogin();
    }

    return throwError(() => error);
  }

  /**
   * Requests that got a 401 while this refresh was running wait on `refreshTokenSubject` for its
   * token. When the refresh fails they fail with it instead of waiting forever; later refreshes
   * get a fresh subject.
   */
  private releaseWaitingRequests(error: HttpErrorResponse) {
    const waiting = this.refreshTokenSubject;
    this.refreshTokenSubject = new BehaviorSubject<any>(null);
    waiting.error(error);
  }

  private addAuthenticationToken(request: HttpRequest<any>) {
    // Get access token from Local Storage
    const accessToken = this.lsService.get('accessToken');

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!accessToken) {
      return request;
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private isGoogleAuthRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/login/google');
  }

  private clearAuthData() {
    this.sessionLifecycle.reset();

    this.lsService.remove('accessToken');
    this.lsService.remove('refreshToken');
    this.lsService.remove('deviceId');
    this.lsService.remove('expiresIn');
  }
}
